import React, { useState, useEffect } from 'react';
import { FlaskRound, Package, Tag, ShoppingCart, TrendingUp, Calendar, Phone, User, MapPin, Home } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CatalogDashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/catalog-stats`, { withCredentials: true });
        setStats(data);
      } catch { toast.error('Failed to load stats'); }
      setLoading(false);
    })();
  }, []);

  if (loading) return <p className="text-sm text-[#94A3B8]">Loading dashboard...</p>;
  if (!stats) return <p className="text-sm text-red-500">Failed to load dashboard</p>;

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard icon={FlaskRound} label="Lab Tests" value={stats.active_tests} sub={`${stats.total_tests} total`} color="bg-sky-50 text-sky-600" />
        <StatCard icon={Package} label="Health Profiles" value={stats.active_profiles} sub={`${stats.total_profiles} total`} color="bg-violet-50 text-violet-600" />
        <StatCard icon={Tag} label="Active Offers" value={stats.tests_with_offer} sub="tests with offer price" color="bg-green-50 text-green-600" />
        <StatCard icon={ShoppingCart} label="Total Orders" value={stats.total_orders} sub={`Rs.${Number(stats.total_revenue || 0).toLocaleString('en-IN')}`} color="bg-amber-50 text-amber-600" />
        <StatCard icon={TrendingUp} label="Categories" value={stats.test_categories} sub={`${stats.profile_categories} profile cats`} color="bg-rose-50 text-rose-600" />
      </div>

      {/* Coverage Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-bold text-[#0A2540] text-sm mb-3">Test Catalog Coverage</h3>
          <div className="space-y-3">
            <ProgressBar label="Active Tests" value={stats.active_tests} total={stats.total_tests} color="bg-sky-500" />
            <ProgressBar label="With Offer Price" value={stats.tests_with_offer} total={stats.total_tests} color="bg-green-500" />
            <ProgressBar label="Profile Mappings" value={stats.total_mappings} total={stats.total_tests * 3} color="bg-violet-500" />
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-bold text-[#0A2540] text-sm mb-3">Quick Summary</h3>
          <div className="space-y-2">
            <SummaryRow label="Total Lab Tests" value={stats.total_tests} />
            <SummaryRow label="Active Lab Tests" value={stats.active_tests} />
            <SummaryRow label="Tests with Offers" value={stats.tests_with_offer} />
            <SummaryRow label="Health Profiles" value={stats.total_profiles} />
            <SummaryRow label="Active Profiles" value={stats.active_profiles} />
            <SummaryRow label="Test-Profile Links" value={stats.total_mappings} />
            <SummaryRow label="Test Categories" value={stats.test_categories} />
            <SummaryRow label="Profile Categories" value={stats.profile_categories} />
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <h3 className="font-bold text-[#0A2540] text-sm mb-3">Recent Bookings ({stats.total_orders})</h3>
        {(!stats.recent_orders || stats.recent_orders.length === 0) ? (
          <p className="text-sm text-[#94A3B8]">No orders yet</p>
        ) : (
          <div className="space-y-2">
            {stats.recent_orders.map((o, i) => (
              <div key={o.id || i} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-xl" data-testid={`recent-order-${i}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-amber-50 rounded-lg flex-shrink-0"><ShoppingCart className="h-4 w-4 text-amber-600" /></div>
                  <div className="min-w-0">
                    <p className="font-medium text-[#0A2540] text-sm truncate">
                      {o.customer_name || 'Unknown'}
                    </p>
                    <div className="flex flex-wrap gap-x-3 text-[11px] text-[#64748B]">
                      {o.phone && <span className="flex items-center gap-0.5"><Phone className="h-2.5 w-2.5" />{o.phone}</span>}
                      {o.home_collection && <span className="flex items-center gap-0.5 text-green-600"><Home className="h-2.5 w-2.5" />Home</span>}
                      <span>{o.created_at?.split('T')[0]}</span>
                    </div>
                    <p className="text-[10px] text-[#94A3B8] truncate">
                      {(o.tests || []).map(t => t.test_name || t.name).join(', ')}
                    </p>
                  </div>
                </div>
                <span className="font-bold text-[#0A2540] text-sm whitespace-nowrap">Rs.{o.total_amount || 0}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={`p-2 rounded-lg ${color} w-fit mb-2`}><Icon className="h-4 w-4" /></div>
    <p className="text-2xl font-bold text-[#0A2540]">{value}</p>
    <p className="text-xs font-medium text-[#0A2540]">{label}</p>
    <p className="text-[10px] text-[#94A3B8]">{sub}</p>
  </div>
);

const ProgressBar = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-[#475569]">{label}</span>
        <span className="font-bold text-[#0A2540]">{value} / {total} ({pct}%)</span>
      </div>
      <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value }) => (
  <div className="flex justify-between text-sm py-1.5 border-b border-[#F1F5F9] last:border-0">
    <span className="text-[#475569]">{label}</span>
    <span className="font-bold text-[#0A2540]">{value}</span>
  </div>
);

export default CatalogDashboard;
